import {
  BLANK_QUOTE, initSections, duplicateQuoteData, defaultFollowUp, applyTravelRule, overlayJobIntoQuote, migrateQuote,
} from "./utils.js";
import {
  calcTotals, quoteLineGroups, quoteContactBlocker, changeOrderContact, amendContact, restoreContactFields,
  isActiveQuoteRecord, withLinkedContactEmail, latestById, quoteClientMatch, quoteSaveBlocker,
} from "./builderHelpers.js";
import {
  buildQuoteSaveRecord, buildDraftSaveRecord, stripListRowKeys, withListOwnedFields, LIST_OWNED_FIELDS, removedAttachmentPaths,
} from "./quoteRecord.js";
import { toDraftRecord, draftByteLength, draftImagePreviews, DRAFT_MAX_BYTES } from "./draftRecord.js";
import { mapProspectToQuote } from "./prospectQuote.js";
import { snapshotQuoteForVersion, applyVersionToQuote, isLegacyVersion, isMarkerVersion, mkVersionId } from "./quoteVersions.js";
import { quoteContentSig, blankContentSig, differsFromBlank } from "./quoteSignature.js";
import { deadLinkPatch, confirmedPrimaryMatch } from "./quoteParty.js";
import { planContactCommits } from "./contactCommit.js";

/* ═══ QUOTE SAVES AND BUILDER REPLACEMENT (docs/quotes/quote-saving.md) ═══
   Everything that writes a quote, or replaces the one open in the builder, lives here so
   it can be tested with a fake Firestore. DataProvider builds one instance and hands its
   functions out through useData(); the quotes test fixture builds one the same way.

   The rules, in one place:
   - One thing at a time. While a save, a booking, a restore or a replacement runs, the
     builder and that quote's list buttons are locked with a visible message, with a time
     limit and a plain failure message. Nothing else can change the quote meanwhile, so
     there is no "newer edits while saving" to reason about.
   - What is clean. The builder knows the content it loaded or last saved, with the stored
     record's savedAt. Dirty means the content differs from that. The dialog asks only then.
   - The recovery copy is harmless. It is stamped with the quote id and the savedAt it was
     based on, so a copy older than the saved record never opens as editable work. Saving,
     Discard and New don't depend on deleting it.
   - A save refuses when the stored record was saved elsewhere since it was opened.

   Every read goes through a getter, so code running after an await sees current data. */

export const RECOVERY_KEY = "nycap-draft";
export const LOCK_TIMEOUT_MS = 25_000;

export const BUSY_MESSAGE = "Wait for the save to finish, then try again.";
export const CHANGED_ELSEWHERE_MESSAGE = "This quote was saved from another window or computer since it was opened here, so it was not saved. Your edits stay on screen: copy what you need from them, then use Discard edits to load the newer version and make the edits again.";
export const DELETED_ELSEWHERE_MESSAGE = "This quote was deleted from another window or computer since it was opened here, so it was not saved. Your edits stay on screen; copy what you need from them. This quote no longer exists.";
export const UNSAVED_EDITS_NOTICE = "This quote is open in the builder with edits that aren't saved.";

const LOCK_LABELS = { saving: "Saving", booking: "Booking", restoring: "Restoring", opening: "Opening", updating: "Updating" };

// After the time limit the quote stays locked until its write lands: a retry can't start
// while the old write can still land. The recovery copy written at that moment keeps the
// edits, so a reload loses nothing (the revision check and the copy's stamp decide what's
// current afterwards).
// `copy` is the recovery copy's state at that moment: "none" (no unsaved edits on screen),
// "writing", "written", or failed ("failed", "too-large"), so the message never promises a
// copy that isn't stored.
const stuckMessage = (kind, ms, copy) => {
  const head = `${LOCK_LABELS[kind] || "Saving"} did not finish after ${Math.round(ms / 1000)} seconds. This quote stays locked until it does. Check the connection.`;
  const jobs = kind === "booking" ? " Check Jobs before booking again." : "";
  if (copy === "none") return `${head} If it doesn't finish, reload the page: there are no unsaved edits on screen to lose.${jobs}`;
  if (copy === "discard-pending") return `${head} If it doesn't finish, reload the page: the edits you chose to discard are still in the recovery copy and will be offered again, so discard them again then.`;
  if (copy === "written") return `${head} If it doesn't finish, reload the page: your edits are kept in the recovery copy.${jobs}`;
  if (copy === "writing") return `${head} The recovery copy of your edits is being written now; wait for it before reloading.${jobs}`;
  if (copy === "files-failed") return `${head} The recovery copy of your edits is written, but a file that hasn't uploaded could not be kept with it: a reload would lose that file, so keep a copy of it first.${jobs}`;
  return `${head} The recovery copy of your edits could not be written, so a reload would lose them: copy what you need first.${jobs}`;
};

const withVersionsOf = (kept, others) => {
  const ids = new Set((kept || []).map((v) => v.id));
  return [...(kept || []), ...(others || []).filter((v) => !ids.has(v.id))];
};

// A website attachment stored without an id gets a stable one (its position), so two
// loads of the same record, or a recovery copy and the record it was based on, read as
// the same content.
const normalizeLoadedAttachments = (list) => list.map((a, i) => ({
  ...a,
  id: a.id || ("att_web_" + i),
  type: a.type && a.type.startsWith("image/") ? "image" : a.type,
  includeInAddendum: a.includeInAddendum !== undefined ? a.includeInAddendum : (a.url ? true : false),
  caption: a.caption || "",
}));

const keyedLines = (lines) => (lines || []).map((li, i) => ({ ...li, key: Date.now() + i }));

const pickListFields = (q) => Object.fromEntries(LIST_OWNED_FIELDS.map((f) => [f, q?.[f]]));

export function createQuotePersistence(deps) {
  const {
    isAdmin, getQ, setQ, getQuotes, setQuotes, getJobs, getContacts, getClients,
    storage, getImages, saveImages, notify, commitContactMatches, syncJobRevenueFromQuote,
    getEquipmentMap, clearHighlights = () => {}, askUnsaved, getSaveBlocker = () => "",
    deleteAttachmentFile = async () => {}, onBusyChange = () => {}, lockTimeoutMs = LOCK_TIMEOUT_MS,
  } = deps;

  /* ── The clean state ──────────────────────────────────────────────── */
  // What the open quote looked like when it was loaded or last saved (`quote`, with its
  // content signature), and the stored record's revision a save checks against
  // ({ exists, savedAt }). The list-owned fields of `quote` say what the builder loaded,
  // so a save can tell an edit from a list action. A baseline can be set for content the
  // builder starts out differing from (a recovered copy, an amendment): dirty from the
  // start, against the revision those edits were made on, never against whatever the
  // list holds later.
  let clean = null;
  const setBaseline = (quote, revision) => {
    clean = { id: quote.id, quote, sig: quoteContentSig(quote), revision: { exists: !!revision?.exists, savedAt: revision?.savedAt ?? null }, listFields: pickListFields(quote) };
  };
  const markClean = (q, { savedAt = null, exists = true } = {}) => setBaseline(q, { exists, savedAt });
  // The form a stored record opens as. Attachments are normalized here, so every baseline
  // built from a record (a load, a recovery copy's, a quote installed by someone else)
  // reads the same as the quote on screen.
  const loadedForm = (saved) => {
    const loaded = {
      ...BLANK_QUOTE(), ...stripListRowKeys(saved), step: "review", savedToDb: false,
      lineItems: keyedLines(saved.lineItems),
      sections: saved.sections || initSections(),
      // The block list: stamped here so a quote saved before it existed derives it from
      // its brief + lines instead of opening with BLANK_QUOTE's empty list (2026-09-04).
      lineGroups: quoteLineGroups(saved),
    };
    if (loaded.attachments) loaded.attachments = normalizeLoadedAttachments(loaded.attachments);
    return loaded;
  };
  // A draft reopens on the step it was left on (the paste step for a draft from AI Start).
  const draftForm = (draft) => {
    const loaded = {
      ...BLANK_QUOTE(), ...stripListRowKeys(draft),
      step: draft.step || "input",
      savedToDb: false,
      lineItems: keyedLines(draft.lineItems),
      sections: draft.sections || initSections(),
      lineGroups: quoteLineGroups(draft),
    };
    if (loaded.attachments) loaded.attachments = normalizeLoadedAttachments(loaded.attachments);
    return loaded;
  };
  const recordOf = (id) => getQuotes().find((sq) => sq.id === id) || null;
  // The baseline for a quote the builder holds: its own clean mark, else the saved record
  // of the same id (a recovered copy, a quote installed by someone else), else blank.
  const recordRevision = (record) => ({ exists: true, savedAt: record?.savedAt ?? null });
  const baselineFor = (q) => {
    if (clean?.id === q?.id) return clean;
    const record = recordOf(q?.id);
    if (record) {
      const form = loadedForm(record);
      return { id: q.id, quote: form, sig: quoteContentSig(form), revision: recordRevision(record), listFields: pickListFields(form) };
    }
    const blank = BLANK_QUOTE();
    return { id: q?.id, quote: blank, sig: blankContentSig(q), revision: { exists: false, savedAt: null }, listFields: pickListFields(blank) };
  };
  // The list-owned fields the builder left as loaded: the stored document keeps its own
  // values for those, read inside the save's transaction.
  const unchangedListFields = (q, base) => LIST_OWNED_FIELDS.filter((f) => JSON.stringify(q?.[f] ?? null) === JSON.stringify(base.listFields[f] ?? null));
  // A quote whose picture without a stored file could not be cached by its last Save
  // draft or draft filing (keepPictures): the picture's bytes are only on screen, so the
  // quote counts as unsaved work (a replacement files it again and retries the cache,
  // a stuck lock writes the copy) until the cache takes them or the quote is replaced.
  let picturesUnkept = null;
  // Attachments with no stored file yet (the upload failed or hasn't run): a picture's
  // preview can go to the image cache; anything else exists only on screen.
  const pendingFiles = (q) => (q?.attachments || []).filter((a) => a && !a.url && !a.storagePath);
  const isDirty = (q) => !!q && (quoteContentSig(q) !== baselineFor(q).sig || (picturesUnkept === q.id && pendingFiles(q).length > 0));
  // Whether the quote on screen was loaded from a stored record, whatever the list holds
  // now (its row may have left: deleted elsewhere). Discard edits is offered on that too.
  const hadRecord = (q) => !!q && !!baselineFor(q).revision.exists;
  const isSavedOrBooked = (q) => !!q && (
    getQuotes().some((sq) => sq.id === q.id && isActiveQuoteRecord(sq))
    || getJobs().some((j) => j.quoteId === q.id)
    || q.jobStatus === "booked");
  // The blank quote the provider starts on. A recovered copy may only replace this, untouched.
  const initial = (() => { const q = getQ(); return { id: q?.id, sig: q ? quoteContentSig(q) : "" }; })();
  if (getQ() && !differsFromBlank(getQ())) markClean(getQ(), { exists: false });

  /* ── The lock ─────────────────────────────────────────────────────── */
  let busy = null;
  let lockSeq = 0;
  // `targetId`: the quote being opened by a replacement, locked with the outgoing one so a
  // list action or a delete can't take it away while it opens.
  const busyView = (b) => (b ? { kind: b.kind, quoteId: b.quoteId, targetId: b.targetId || null, label: LOCK_LABELS[b.kind], stuck: !!b.stuck, copy: b.copy || null, message: b.stuck ? stuckMessage(b.kind, lockTimeoutMs, b.copy) : "" } : null);
  const setBusy = (next) => { busy = next; onBusyChange(busyView(next)); };
  const busyError = () => { const e = new Error(BUSY_MESSAGE); e.code = "busy"; e.userMessage = BUSY_MESSAGE; return e; };
  /** Is this quote held by the lock, as the quote being saved or the one being opened? */
  const isLocked = (id) => !!id && !!busy && (busy.quoteId === id || busy.targetId === id);
  // Runs `task(current)` with the builder locked, until the task is done. After the time
  // limit the lock stays (nothing else may start while this write can still land): the
  // lock is marked stuck, the operator is told to check the connection and reload if need
  // be, and the recovery copy is written then so the edits survive a reload (the message
  // follows that write: being written, written, or failed). When the write lands after
  // all, the builder settles as it would have. `current()` is kept for the tasks' own
  // checks; under this rule it is true for as long as the task runs.
  const locked = async (kind, quoteId, task, { targetId = null } = {}) => {
    if (busy) throw busyError();
    const token = ++lockSeq;
    setBusy({ kind, quoteId, targetId, token });
    const current = () => busy?.token === token;
    const timer = setTimeout(() => {
      if (!current()) return;
      // A copy is written only when the screen holds unsaved edits (a replacement starts
      // from a clean builder; a booking from the list may run beside a clean one): with
      // none, a reload loses nothing and the message says so.
      // A replacement starts from a clean builder (its edits were saved, filed or
      // discarded), so its copy holds only the writes that were waiting for the lock,
      // on the clean baseline: a discarded edit is not brought back by it.
      const live = getQ();
      const waiting = !!live && deferredFor(live.id).length > 0;
      const writing = !!live && (kind === "opening" ? waiting : (isDirty(live) || waiting));
      stuckSnapshot = kind === "opening" ? () => ({ ...baselineFor(getQ()).quote, id: getQ().id }) : () => getQ();
      // A replacement after Discard whose copy still holds the discarded edits (the
      // marker is written only once the replacement lands): a reload offers them again.
      // The recovery key is one for all quotes: a marker still on its way for any quote
      // means a reload may offer those discarded edits again, whatever is open now.
      const discarding = (kind === "opening" && discardPending) || !!markerPending;
      const copy = writing ? "writing" : (discarding ? "discard-pending" : "none");
      setBusy({ ...busy, stuck: true, copy });
      notify.error(stuckMessage(kind, lockTimeoutMs, copy));
      if (writing) writeStuckCopy(current);
    }, lockTimeoutMs);
    try {
      return await task(current);
    } finally {
      clearTimeout(timer);
      if (current()) setBusy(null);
      // A write that waited for the lock lands now, after the settle retired the copy:
      // it is unsaved work from this moment, so its copy is written at once (stamped with
      // the revision just stored), not 800 ms later by the autosave.
      const waited = deferredFor(getQ()?.id).length > 0;
      flushDeferred();
      if (waited && shouldAutosave(getQ())) writeRecoveryCopy(getQ());
    }
  };
  const getBusy = () => busyView(busy);

  // Writes that arrive on their own (an upload finishing) wait while the builder is locked
  // and land afterwards, on the quote they were for. If another quote is open by then, the
  // write is dropped and the caller is told.
  const deferred = [];
  const applyUpdate = ({ quoteId, updater, onDropped, onApplied }) => {
    const live = getQ();
    if (live?.id !== quoteId) { onDropped?.(); return "dropped"; }
    setQ(updater(live));
    onApplied?.();
    return "applied";
  };
  const flushDeferred = () => { while (deferred.length && !busy) applyUpdate(deferred.shift()); };
  const deferredFor = (quoteId) => deferred.filter((d) => d.quoteId === quoteId);
  // The quote as it will be once the writes waiting for the lock have landed: what a
  // stuck lock's recovery copy must hold, since a reload drops the waiting writes.
  const withDeferred = (q) => deferredFor(q?.id).reduce((acc, d) => d.updater(acc), q);
  // The stuck lock's recovery copy: the screen with the waiting writes folded in. A write
  // that queues after the copy was written puts it back to "writing" and writes it again.
  let copyGen = 0;
  // What the stuck lock's copy is made of: the screen (a save), or the clean baseline (a
  // replacement); set by the lock's timer. `stuckCopy` is the last such write, by quote:
  // a replacement that lands after writing one for its outgoing quote drops the writes
  // that copy held, so the copy is marked discarded once that write has settled.
  let stuckSnapshot = () => getQ();
  let stuckCopy = null;
  const writeStuckCopy = (current) => {
    const base = stuckSnapshot();
    if (!base) return;
    // An unseen copy of another quote is held: nothing writes over it, and the message
    // says a reload would lose what is on screen.
    if (recoveryHeld && heldCopyFor !== base.id) { setBusy({ ...busy, copy: "failed" }); return; }
    if (busy?.copy !== "writing") setBusy({ ...busy, copy: "writing" });
    // Only the latest write's outcome counts: an earlier one landing after a later one
    // was started must not say the newer work is kept.
    const gen = ++copyGen;
    const write = writeRecoveryCopy(withDeferred(base));
    stuckCopy = { quoteId: base.id, write };
    write.then((outcome) => {
      if (!current() || gen !== copyGen) return;
      setBusy({ ...busy, copy: outcome });
      // A failed copy is said by writeRecoveryCopy itself (once per failing streak);
      // the overlay and the dialogs carry the lock's own message meanwhile.
    });
  };
  // onApplied runs when the write lands (now, or after the lock lifts); onDropped when it
  // can't (another quote is open by then).
  const updateQuote = (quoteId, updater, { onDropped, onApplied } = {}) => {
    // Another quote is open by now: dropped at once, lock or no lock (a write held behind
    // an unrelated quote's lock would be lost by the reload that lock may ask for).
    if (getQ()?.id !== quoteId) { onDropped?.(); return "dropped"; }
    if (busy) {
      deferred.push({ quoteId, updater, onDropped, onApplied });
      if (busy.stuck && getQ()?.id === quoteId) { const token = busy.token; writeStuckCopy(() => busy?.token === token); }
      return "deferred";
    }
    return applyUpdate({ quoteId, updater, onDropped, onApplied });
  };
  /** Is this quote the one open in the builder? Deleting it from a list would strand its edits. */
  const isOpenQuote = (id) => !!id && getQ()?.id === id;
  // A change that isn't an edit (a dead link cleared once the CRM loaded, a list action
  // on the open quote): applied, and the clean mark follows it when the quote was clean.
  const normalizeOpenQuote = (patch) => {
    const q = getQ();
    if (!q || !patch) return;
    const base = baselineFor(q);
    const next = { ...q, ...patch };
    setQ(next);
    // The baseline takes the same patch (only the keys in it), so the quote is as clean or
    // as dirty as it was: a hand edit undone afterwards leaves it clean.
    setBaseline({ ...base.quote, ...patch }, base.revision);
  };

  // Anyone holding work against the open quote (the AI box) drops it when a switch begins.
  const replaceListeners = new Set();
  const onReplaceStart = (fn) => { replaceListeners.add(fn); return () => replaceListeners.delete(fn); };
  const announceReplace = () => { replaceListeners.forEach((fn) => fn()); };

  /* ── Shared pieces ────────────────────────────────────────────────── */
  const upsertSaved = (record) => setQuotes((prev) => {
    const idx = prev.findIndex((e) => e.id === record.id);
    return idx >= 0 ? prev.map((e) => (e.id === record.id ? record : e)) : [...prev, record];
  });

  // The image cache holds one map per quote and a write replaces it whole (imageStorage
  // saveImages), so every write here merges into what is stored: a picture whose bytes
  // are only in the cache (not on screen yet, not uploaded) is never dropped by a write
  // that doesn't carry it.
  const savePreviews = async (quoteId, previews) => {
    if (!previews.length) return;
    let existing = null;
    try { existing = await getImages(quoteId); } catch (e) { console.warn("Image cache read:", e); }
    const ids = new Set(previews.map((p) => p.id));
    const kept = Object.entries(existing || {}).filter(([id]) => !ids.has(id)).map(([id, dataUrl]) => ({ id, dataUrl }));
    await saveImages(quoteId, [...kept, ...previews]);
  };
  const cacheImages = async (record, attachments) => {
    // Image dataUrls are cached locally so they re-display instantly on reload without
    // refetching from Storage (the durable copy lives in Storage via attachment.url).
    try {
      const imgs = (attachments || []).filter((a) => a.type === "image" && a.dataUrl).map((a) => ({ id: a.id, dataUrl: a.dataUrl }));
      if (imgs.length) await savePreviews(record.id, imgs);
    } catch (e) { console.warn("Image cache save:", e); }
  };

  const withCachedImages = async (quoteId, loaded) => {
    try {
      const imgMap = await getImages(quoteId);
      if (imgMap && loaded.attachments) {
        loaded.attachments = loaded.attachments.map((a) => (a.type === "image" && imgMap[a.id] ? { ...a, dataUrl: imgMap[a.id] } : a));
      }
    } catch (e) { console.warn("Image load:", e); }
    return loaded;
  };

  // A save replaces the whole version list. What's stored by then (a Restore's entries) stays.
  const withSavedHistory = (record) => {
    const stored = recordOf(record.id)?.versions;
    return stored?.length ? { ...record, versions: withVersionsOf(stored, record.versions) } : record;
  };

  // Files an attachment removal left behind are deleted after the save that drops them, never
  // at removal time, so Discard can bring a removed picture back.
  const deleteDroppedFiles = (prevSaved, record) => {
    for (const path of removedAttachmentPaths(prevSaved, record)) {
      Promise.resolve().then(() => deleteAttachmentFile(path)).catch((e) => console.warn("Attachment file delete:", e));
    }
  };

  // Returns the record as committed: what the store holds now, the fields it kept included.
  const saveRecord = async (record, revision, keepFromStored = []) => {
    try {
      const result = await storage.quotes.saveChecked(record, revision, { keepFromStored });
      return result?.record ? stripListRowKeys({ ...record, ...result.record, _docId: result.record._docId }) : record;
    } catch (e) {
      if (e?.code === "changed-elsewhere" || e?.code === "deleted-elsewhere") {
        const message = e.code === "deleted-elsewhere" ? DELETED_ELSEWHERE_MESSAGE : CHANGED_ELSEWHERE_MESSAGE;
        const err = new Error(message);
        err.code = e.code;
        err.userMessage = message;
        throw err;
      }
      throw e;
    }
  };

  // The builder after a save: the stored record, with the builder's line keys kept so the
  // builder (and the AI box) still find each line.
  // The stored record is lean (no picture bytes, no file being uploaded): the screen
  // keeps those from the quote it showed, by attachment id.
  const mergedAfterSave = (live, record) => ({
    ...live,
    ...record,
    lineItems: (record.lineItems || []).map((li, i) => ({ ...li, key: live?.lineItems?.[i]?.key ?? Date.now() + i })),
    attachments: (record.attachments || []).map((a) => {
      const shown = (live?.attachments || []).find((x) => x?.id === a.id);
      return shown ? { ...a, ...(shown.dataUrl && !a.dataUrl ? { dataUrl: shown.dataUrl } : {}), ...(shown.file ? { file: shown.file } : {}) } : a;
    }),
    savedToDb: true,
  });

  /* ── The recovery copy ────────────────────────────────────────────── */
  // Written by the autosave while the open quote holds unsaved edits. Stamped with the
  // quote id and the savedAt it was based on, so a copy older than the saved record is
  // never opened as editable work.
  const recoveryCopyFor = (q) => {
    const { revision, listFields, quote: base } = baselineFor(q);
    // baseListFields: the list-owned fields as the builder loaded them, so on install a
    // list action made elsewhere meanwhile can be told from an edit the builder made.
    // baseLinks: the contact and client links as loaded, so an Unlink can be told from a
    // link a save filled in.
    const baseLinks = { contactEntityId: base?.contactEntityId ?? null, companyEntityId: base?.companyEntityId ?? null };
    return { v: 2, quoteId: q.id, basedOnSavedAt: revision.savedAt, basedOnRecord: revision.exists, baseListFields: listFields, baseLinks, writtenAt: new Date().toISOString(), quote: toDraftRecord(q) };
  };
  const shouldAutosave = (q) => !!q && isAdmin() && !busy && !recoveryPending && !recoveryHeld && isDirty(q);
  // Edits undone by hand leave the quote clean while their copy is still stored; it would
  // bring them back on a reload, so it is marked discarded (never awaited).
  let copyWrittenFor = null;
  const retireIfClean = (q) => {
    if (!q || copyWrittenFor !== q.id || busy || isDirty(q)) return;
    copyWrittenFor = null;
    // A recovered copy made on an older save, undone by hand until the screen equals the
    // record as it is now (content compared, not only the old baseline): the work is based
    // on the record, so its revision is the record's. A screen that equals only the older
    // save keeps the older revision, and a save of it is refused.
    const record = recordOf(q.id);
    if (record && clean?.id === q.id && (clean.revision.savedAt ?? null) !== (record.savedAt ?? null)) {
      const form = loadedForm(record);
      Object.assign(form, deadLinkPatch(form, getContacts(), getClients()) || {});
      if (quoteContentSig(q) === quoteContentSig(form)) markClean(q, { savedAt: record.savedAt ?? null });
    }
    discardRecoveryCopy(q.id);
  };
  let recoveryFailing = false;
  let picturesFailing = false;
  const writeRecoveryCopy = async (q) => {
    recoveryGen += 1;
    const copy = recoveryCopyFor(q);
    copyWrittenFor = q.id;
    const bytes = draftByteLength(copy);
    if (bytes > DRAFT_MAX_BYTES) {
      console.warn(`Recovery copy skipped: ${bytes} bytes exceeds ${DRAFT_MAX_BYTES} (Firestore field cap).`);
      if (!recoveryFailing) notify.error("This quote is too large for its recovery copy. A reload would lose your edits, so save it with Save draft or Ingest.");
      recoveryFailing = true;
      return "too-large";
    }
    try {
      await storage.kv.set(RECOVERY_KEY, copy);
      recoveryFailing = false;
    } catch (e) {
      console.warn("Recovery copy failed:", e);
      if (!recoveryFailing) notify.error("The recovery copy of this quote could not be written. A reload would lose your edits, so save it with Save draft or Ingest.");
      recoveryFailing = true;
      return "failed";
    }
    // A file's bytes aren't in the copy (Firestore's size cap): a picture's preview goes
    // to the image cache; anything else (a PDF, a picture with no preview) has no cache.
    // A file that hasn't uploaded yet exists nowhere else, so the copy counts as written
    // only once every such file is in the cache; otherwise the outcome says so.
    const previews = draftImagePreviews(q);
    let cached = true;
    if (previews.length) {
      try {
        await savePreviews(q.id, previews);
      } catch (e) {
        console.warn("Draft image cache save:", e);
        cached = false;
      }
    }
    const previewIds = new Set(previews.map((p) => p.id));
    const lost = pendingFiles(q).some((a) => !previewIds.has(a.id) || !cached);
    if (lost) {
      // Said once per failing streak, from the autosave and the stuck lock alike (the
      // lock's overlay carries its own message).
      if (!picturesFailing) notify.error("The recovery copy of this quote is written, but a file that hasn't uploaded could not be kept with it. A reload would lose that file: save the quote with Save draft, or wait for the upload or retry it first.");
      picturesFailing = true;
      return "files-failed";
    }
    picturesFailing = false;
    return "written";
  };
  // Best effort, never awaited by a save or a replacement: a copy left behind is harmless
  // once the record it was based on has a newer savedAt.
  // Never while an unseen copy of another quote is held (recoveryUnknown): that copy is
  // the one thing the key must keep until a reload shows it.
  const retireRecoveryCopy = () => {
    if (recoveryHeld && heldCopyFor !== getQ()?.id) return Promise.resolve();
    return storage.kv.delete(RECOVERY_KEY).catch((e) => console.warn("Recovery copy delete:", e));
  };
  // Discarded edits don't move the record's revision, so their copy would still match it.
  // The copy is marked discarded (a write), else deleted; if neither lands, it's said, so a
  // reload offering the discarded edits again is no surprise. Never awaited.
  // `markerPending`: the quote whose discarded marker is still on its way (never awaited
  // by a save, a replacement or a restore): a lock stuck meanwhile says a reload may
  // offer the discarded edits again, since the old copy is still the stored one.
  // A count: two Discards in a row each have their own marker on the way, and the flag
  // holds until the last of them has settled.
  let markerPending = 0;
  // `recoveryGen` counts the copy writes started: a marker's fallback delete, issued after
  // its write failed, must not remove a copy that a later edit started writing meanwhile.
  let recoveryGen = 0;
  const discardRecoveryCopy = (quoteId) => {
    if (recoveryHeld && heldCopyFor !== quoteId) return Promise.resolve();
    markerPending += 1;
    const gen = recoveryGen;
    const settled = () => { markerPending -= 1; };
    return storage.kv.set(RECOVERY_KEY, { v: 2, quoteId, discarded: true, writtenAt: new Date().toISOString() })
      .catch((e) => {
        if (recoveryGen !== gen) { console.warn("Recovery copy discard (a newer copy since):", e); return undefined; }
        return storage.kv.delete(RECOVERY_KEY);
      })
      .catch((e) => {
        console.warn("Recovery copy discard:", e);
        notify.error("The recovery copy of the discarded edits could not be cleared. After a reload they may be offered again; discard them again then.");
      })
      .finally(settled);
  };

  // The recovery key could not be read on load (DataContext's loadAll says so): nobody
  // knows whether a copy is stored, so the autosave stays off (it would write over an
  // unseen copy) and the operator is told, until a read succeeds. A copy found then
  // installs over the untouched blank; over a builder that has moved on it stays stored,
  // the autosave stays off, and the operator is told to save and reload.
  let recoveryHeld = false;
  let recoveryRetry = null;
  // The quote whose unseen copy is held: the key's delete, the discarded marker and the
  // stuck copy write for any other quote are skipped until a reload shows it.
  let heldCopyFor = null;
  const recoveryUnknown = (crm = null) => {
    if (recoveryHeld) return;
    recoveryHeld = true;
    notify.error("The recovery copy of unsaved edits could not be read, so edits made now are not autosaved: save them with Save draft or Ingest. It is read again shortly; reload when the connection is back.");
    const again = async () => {
      let copy;
      try { copy = await storage.kv.fetch(RECOVERY_KEY); } catch (e) { console.warn("Recovery copy read:", e); recoveryRetry = setTimeout(again, lockTimeoutMs); return; }
      recoveryRetry = null;
      // Another lock owns the builder (a quote being opened): nothing installs under it;
      // the key stays held and the read runs again after.
      if (busy) { recoveryRetry = setTimeout(again, lockTimeoutMs); return; }
      if (!copy || copy.discarded || untouchedBlank(getQ())) {
        recoveryHeld = false;
        if (copy && !copy.discarded && installRecoveredCopy(copy, crm)) {
          // The pictures' bytes from the cache, merged into the quote as it is then.
          const quoteId = getQ()?.id;
          const hydrated = await withCachedImages(quoteId, { attachments: getQ()?.attachments || [] });
          const now = getQ();
          if (now?.id === quoteId) {
            const previews = Object.fromEntries((hydrated.attachments || []).filter((a) => a?.dataUrl).map((a) => [a.id, a.dataUrl]));
            setQ({ ...now, attachments: (now.attachments || []).map((a) => (a?.dataUrl || !previews[a?.id] ? a : { ...a, dataUrl: previews[a.id] })) });
          }
        }
        return;
      }
      // A copy exists and the builder has moved on: it stays stored, unseen, until a
      // reload shows it; the autosave stays off and nothing else touches the key.
      heldCopyFor = copy.quoteId;
      notify.error(`A recovery copy of unsaved edits for ${copy.quote?.clientName || copy.quoteId} is stored but could not be opened over what is open now. Save what is open (Save draft or Ingest), then reload the page to get it. Edits are not autosaved until then.`);
    };
    recoveryRetry = setTimeout(again, lockTimeoutMs);
  };
  // What a copy may open over: the blank the app started on, or a blank New made while the
  // page was loading, with nothing in it.
  const untouchedBlank = (live) => (live?.id === initial.id && quoteContentSig(live) === initial.sig)
    || (!!live && !differsFromBlank(live) && !isDirty(live) && !hadRecord(live));
  // The builder holds work, so the copy can't open. It stays stored and unseen: the
  // autosave stays off and nothing else touches the key until a reload shows it.
  const holdUnseenCopy = (copy) => {
    recoveryHeld = true;
    heldCopyFor = copy.quoteId;
    notify.error(`A recovery copy of unsaved edits for ${copy.quote?.clientName || copy.quoteId} was not opened because another quote was already open. Save what is open (Save draft or Ingest), then reload the page to get it. Edits are not autosaved until then.`);
  };
  // The store check a copy naming a missing record is waiting on (recoverFromStore):
  // replacements and the autosave wait for it.
  let recoveryPending = null;
  // On load: only over an untouched blank (untouchedBlank), only when it is newer than
  // the saved record it was based on, and only when it differs from that record.
  // `crm`: the contacts and clients as loaded, for a caller that installs the copy before
  // its getters hold them (DataContext's first load); else the getters.
  const installRecoveredCopy = (raw, crm = null) => {
    const contactsNow = crm?.contacts || getContacts();
    const clientsNow = crm?.clients || getClients();
    if (raw?.discarded) return false;
    // An old-shape copy (the quote itself, from before v2) of a quote that had been stored
    // (it carries `_docId`, `savedAt` or `savedToDb`) counts as based on a record: with the
    // record absent it goes to the store check below and can never be saved as a new
    // quote (that would recreate a deleted one).
    const copy = raw?.v === 2 && raw.quote ? raw : (raw?.id ? { quoteId: raw.id, basedOnSavedAt: undefined, basedOnRecord: !!(raw._docId || raw.savedAt || raw.savedToDb), quote: raw } : null);
    if (!copy?.quoteId) return false;
    if (!untouchedBlank(getQ())) {
      holdUnseenCopy(copy);
      return false;
    }
    const record = recordOf(copy.quoteId);
    const who = copy.quote?.clientName || copy.quoteId;
    // A copy made on an older save than the record's: it can never be saved over the
    // newer record (its baseline keeps the revision it was made on, so a save is refused
    // by the revision check), but its edits are not thrown away either. Where it says
    // nothing the record doesn't (the usual case: this computer's own save moved the
    // revision and the copy's retire didn't land), it goes; where it differs (a stuck
    // save on this computer while the other computer saved), it opens so the edits can be
    // copied out, and Discard edits loads the newer version.
    let older = false;
    let trashed = false;
    if (record) {
      const storedAt = record.savedAt ?? null;
      if (copy.basedOnSavedAt === undefined) {
        notify.info(`An older recovery copy of the quote for ${who} was not opened, because that quote has been saved since.`);
        retireRecoveryCopy();
        return false;
      }
      // A trashed quote's copy opens like any other (the one recovery key must not be
      // written over by another quote's autosave while these edits are unseen); the
      // notice says the quote is in the Trash. A save keeps it there; Restore to Open
      // brings it back to the list.
      older = copy.basedOnSavedAt !== storedAt;
      if (record.deletedAt) trashed = true;
    } else if (copy.basedOnRecord) {
      // Edits to a record that isn't in the list: the list's read failed, or the quote
      // was deleted elsewhere. The store is asked directly (recoverFromStore): a record
      // found is installed as usual; a quote that is gone or can't be read opens the copy
      // as edits that can't be saved over anything, so the next quote's autosave never
      // writes over the one recovery key while these edits are still unseen.
      // Replacements (an inquiry link converting on load) and the autosave wait for this
      // answer, so nothing moves the builder on or writes over the copy meanwhile.
      if (!crm?.retried && !recoveryPending) {
        recoveryPending = recoverFromStore(raw, crm).catch((e) => { console.warn("Recovery copy check:", e); return false; }).finally(() => { recoveryPending = null; });
      }
      return false;
    }
    const loaded = { ...BLANK_QUOTE(), ...stripListRowKeys(copy.quote), lineGroups: quoteLineGroups(copy.quote), lineItems: keyedLines(copy.quote.lineItems) };
    // The baseline is the record as the builder would have opened it: a dead contact or
    // client link cleared (quoteParty deadLinkPatch), as the builder's own load does
    // without counting it as an edit, so the copy (made after that clearing) compares
    // against the same thing.
    const baseForm = record ? loadedForm(record) : BLANK_QUOTE();
    if (record) Object.assign(baseForm, deadLinkPatch(baseForm, contactsNow, clientsNow) || {});
    // A list action made elsewhere since the copy (Mark lost, a follow-up) didn't move the
    // revision. Where the copy's list-owned field is what the builder loaded, the stored
    // value stands; where the builder changed it, the copy's value is the edit.
    if (record && copy.baseListFields) {
      for (const f of LIST_OWNED_FIELDS) {
        if (JSON.stringify(copy.quote?.[f] ?? null) === JSON.stringify(copy.baseListFields[f] ?? null)) loaded[f] = baseForm[f];
      }
    }
    // Equal to the record, or equal once a save's own filling is applied: the copy was
    // consumed by that save. Ingest links the client the typed name matches
    // (quoteClientMatch), and links the confirmed contact it wrote to the CRM and takes
    // their email (commitContactMatches, withLinkedContactEmail): a copy with no contact
    // link whose contact name is the record's takes the record's link and email for the
    // comparison, since those come only from that save.
    // "Consumed by its save" is asked only of a copy older than the record (the record was
    // saved since the copy): a copy of the same revision holds plain edits, an unlink
    // included, and compares as it is.
    const sameAsRecord = () => {
      if (!record) return quoteContentSig(loaded) === blankContentSig(loaded);
      if (quoteContentSig(loaded) === quoteContentSig(baseForm)) return true;
      if (!older) return false;
      // A date cleared by hand is an edit a save keeps (Save draft stores the empty date,
      // on an active quote too); a save's projection would fill it, so it is checked
      // first: a copy whose dates differ from the record's is not consumed.
      if ((loaded.quoteDate || "") !== (baseForm.quoteDate || "") || (loaded.followUpDate || "") !== (baseForm.followUpDate || "")) return false;
      // An Unlink: the copy has no link where the builder had loaded one (`baseLinks`). A
      // save keeps an unlink; a projection would fill the link back in from the typed
      // name or a confirmed row, so it is checked first.
      const links = copy.baseLinks || {};
      if ((!loaded.contactEntityId && links.contactEntityId) || (!loaded.companyEntityId && links.companyEntityId)) return false;
      const active = isActiveQuoteRecord(record);
      const filled = { ...loaded };
      // A contact Ingest wrote to the CRM and linked: only a confirmed Contacts Found row
      // could have passed the save check without a link, so only then does the copy take
      // the record's link and email, and only for an active quote (Ingest's save); a
      // plain unlinked name, or any unlink on a draft, stays an unlink.
      if (active && !loaded.contactEntityId && baseForm.contactEntityId && confirmedPrimaryMatch(loaded) && (loaded.contactName || "") === (baseForm.contactName || "")) {
        filled.contactEntityId = baseForm.contactEntityId;
        filled.contactEmail = baseForm.contactEmail;
      }
      // Both sides through the save's own projection, in the save's order: the linked
      // contact's email from the CRM first, then the record a save would store with its
      // own quote date (the client a confirmed Contacts Found row names, else the one the
      // typed name or that email matches; equipment as ids, crew as records, the defaults).
      // A real draft was saved by Save draft, whose projection keeps what the builder
      // holds (an empty date included): the comparison goes through that one.
      const asSaved = (form) => {
        try {
          if (!isActiveQuoteRecord(record)) return quoteContentSig(buildDraftSaveRecord(form, { draft: true }));
          const withEmail = withLinkedContactEmail(form, contactsNow);
          const plan = planContactCommits(withEmail, withEmail.contactMatches, contactsNow, clientsNow);
          const commit = { companyEntityId: (clientsNow || []).some((c) => c.id === plan?.companyEntityId) ? plan.companyEntityId : null };
          const built = buildQuoteSaveRecord(withEmail, { prevSaved: record, quoteDate: withEmail.quoteDate || record.quoteDate, commit, matchedClient: quoteClientMatch(withEmail, clientsNow), equipmentMap: getEquipmentMap() });
          return quoteContentSig(built);
        } catch (e) {
          console.warn("Recovery copy comparison:", e);
          return null;
        }
      };
      const a = asSaved(filled);
      return a !== null && a === asSaved(baseForm);
    };
    if (sameAsRecord()) { retireRecoveryCopy(); return false; }
    setQ(loaded);
    // Dirty from the start, against the revision the edits were made on: a save made
    // elsewhere later is refused, never overwritten. The copy counts as this quote's, so
    // undoing the recovered edits by hand retires it.
    const revision = !record ? { exists: false, savedAt: null } : older ? { exists: true, savedAt: copy.basedOnSavedAt ?? null } : recordRevision(record);
    setBaseline({ ...baseForm, id: loaded.id }, revision);
    copyWrittenFor = loaded.id;
    if (trashed) notify.info(`Recovered edits that weren't saved for ${who}. That quote is in the Trash: a save keeps it there with the edits; Restore to Open in Saved Quotes brings it back to the list.${older ? " It was also saved elsewhere since, so these edits can't be saved over it: copy what you need, then use Discard edits." : ""}`);
    else if (older) notify.info(`Recovered edits that weren't saved for ${who}, made on an older version of this quote: it was saved elsewhere since, so these edits can't be saved over it. Copy what you need from them, then use Discard edits to load the newer version.`);
    else notify.info(`Recovered edits that weren't saved for ${who}. Save them, or choose New and discard them.`);
    return true;
  };

  // The copy names a record the list doesn't hold: the store is read directly. A record
  // found enters the list and the copy installs as usual. None (deleted elsewhere), or a
  // failed read: the copy opens as unsaved edits against the save it was made on, dirty
  // against blank, so a save is refused by the revision check (or checked against the
  // store once it answers), Discard edits clears them, and nothing writes over the copy
  // before it was seen.
  const recoverFromStore = (raw, crm) => {
    const copy = raw?.v === 2 && raw.quote ? raw : (raw?.id ? { quoteId: raw.id, basedOnSavedAt: undefined, quote: raw } : null);
    if (!copy) return Promise.resolve(false);
    const who = copy.quote?.clientName || copy.quoteId;
    if (busy) return Promise.resolve(false);
    // Under the lock from the first moment: the builder is inert and the autosave off until
    // the store has answered, so nothing typed meanwhile can make the copy unseen, and
    // nothing writes over it.
    return locked("opening", getQ()?.id, async () => {
      let stored = null;
      let readFailed = false;
      try { stored = await storage.quotes.fetch(copy.quoteId); } catch (e) { console.warn("Recovery copy record read:", e); readFailed = true; }
      if (!untouchedBlank(getQ())) {
        holdUnseenCopy(copy);
        return false;
      }
      // The pictures' bytes live in the image cache (the copy is lean): put back on the
      // recovered quote either way, so a picture that hasn't uploaded can be seen and
      // copied out, and kept by a later Save draft.
      if (stored) {
        upsertSaved(migrateQuote(stored));
        const ok = installRecoveredCopy(raw, { ...(crm || {}), retried: true });
        if (ok) {
          // The previews are merged into the quote as it is once the cache has answered
          // (a list action delivered meanwhile stays), never the quote as captured before.
          const hydrated = await withCachedImages(copy.quoteId, { attachments: getQ()?.attachments || [] });
          const now = getQ();
          if (now?.id === copy.quoteId) {
            const previews = Object.fromEntries((hydrated.attachments || []).filter((a) => a?.dataUrl).map((a) => [a.id, a.dataUrl]));
            setQ({ ...now, attachments: (now.attachments || []).map((a) => (a?.dataUrl || !previews[a?.id] ? a : { ...a, dataUrl: previews[a.id] })) });
          }
        }
        return ok;
      }
      const lean = { ...BLANK_QUOTE(), ...stripListRowKeys(copy.quote), lineGroups: quoteLineGroups(copy.quote), lineItems: keyedLines(copy.quote.lineItems) };
      const loaded = await withCachedImages(copy.quoteId, lean);
      setQ(loaded);
      setBaseline({ ...BLANK_QUOTE(), id: loaded.id }, { exists: true, savedAt: copy.basedOnSavedAt ?? null });
      copyWrittenFor = loaded.id;
      notify.info(readFailed
        ? `Recovered edits that weren't saved for ${who}. That quote could not be read just now; saving checks it when the connection is back. Copy what you need from them, or use Discard edits.`
        : `Recovered edits that weren't saved for ${who}. That quote is no longer in the list (deleted elsewhere), so these edits can't be saved over it. Copy what you need from them, then use Discard edits.`);
      return true;
    });
  };

  /* ── Ingest ───────────────────────────────────────────────────────── */
  const runIngest = async (q) => {
    const base = baselineFor(q);
    const prevSaved = recordOf(q.id);
    const quoteDate = q.quoteDate || new Date().toISOString().split("T")[0];

    // Create/link the CRM records the operator approved, BEFORE the quote is written, so
    // the ids below are real and a failure is visible at save time rather than discovered
    // weeks later as a missing contact.
    const contactBlock = quoteContactBlocker(q, getContacts());
    if (contactBlock) throw new Error(contactBlock);
    const commit = await commitContactMatches({ ...q, quoteDate }, q.contactMatches);
    // The quote's contact as saved: the picked id, else the commit's, with that contact's
    // current email. Settled before the client lookup, which reads the email.
    const qSave = withLinkedContactEmail(
      { ...q, contactEntityId: q.contactEntityId || commit.contactEntityId || null },
      latestById(getContacts(), commit.written?.contacts),
    );
    // The typed client name wins when it names exactly one account; the contact's email
    // is the fallback (quoteClientMatch).
    const matchedClient = quoteClientMatch(qSave, getClients());
    let record = buildQuoteSaveRecord(qSave, { prevSaved, quoteDate, commit, matchedClient, equipmentMap: getEquipmentMap() });
    record = withSavedHistory(withListOwnedFields(record, { q, baseline: base.listFields, stored: prevSaved }));
    // The confirmed person's record failed to write: the quote would save without its
    // contact, so stop here. Records the commit did write stay; the retry finds them.
    if (!record.contactEntityId) {
      throw new Error("The contact could not be saved to your contacts, so the quote was not saved. Try again.");
    }

    let committed;
    try {
      committed = await saveRecord(record, base.revision, unchangedListFields(q, base));
    } catch (e) {
      if (!e.userMessage) notify.error("Failed to save quote. Please try again.");
      throw e;
    }
    // The quote is stored: the builder and its revision settle now, before the work that
    // follows, so a failure there can't leave the next save clashing with this one.
    upsertSaved(committed);
    settleBuilder(q.id, committed);
    // Housekeeping runs on its own: it can't turn a stored save into a time limit, and
    // the next save doesn't wait for it.
    afterSave(committed, q, prevSaved, commit).catch((e) => console.error("After save:", e));
    return committed;
  };

  // What follows a stored quote: the image cache, the CRM notice, the job's revenue and the
  // files a removal left behind. The quote is saved whatever happens here, so a failure
  // is reported and doesn't fail the save.
  // The job's revenue follows each save in the order the saves were stored, per quote,
  // whatever else (the image cache) is still running: an older save's sync can't land
  // after a newer one's.
  // One sync runs per quote at a time, for the latest total asked for; totals asked for
  // meanwhile coalesce into the next run. A sync that hasn't answered after the time
  // limit is said (the job's revenue may be stale), and the next run doesn't wait for it;
  // when it does answer, the latest total is synced again if it wasn't the one it wrote,
  // so money ends on the newest stored total whatever order the writes landed in.
  const revenueState = new Map();
  const syncRevenueInOrder = (quoteId, total, what) => {
    const st = revenueState.get(quoteId) || { latest: null, running: null, requested: undefined };
    revenueState.set(quoteId, st);
    st.latest = { total, what };
    st.requested = total;
    return kickRevenue(quoteId);
  };
  const kickRevenue = (quoteId) => {
    const st = revenueState.get(quoteId);
    if (!st || !st.latest) return st?.running || Promise.resolve(null);
    if (st.running) return st.running;
    const { total, what } = st.latest;
    st.latest = null;
    const sync = (async () => {
      try {
        const revenueSync = await syncJobRevenueFromQuote(quoteId, total);
        if (revenueSync?.skipped) notify.error(`${what}. Job is invoiced or paid - gross revenue not updated.`);
        return revenueSync;
      } catch (e) {
        console.error("Job revenue sync after save:", e);
        notify.error(`${what}, but the job's revenue could not be checked. Open the job to check its revenue.`);
        return null;
      }
    })();
    let timer = null;
    const limit = new Promise((resolve) => { timer = setTimeout(() => resolve("timeout"), lockTimeoutMs); });
    const run = Promise.race([sync, limit]).then((result) => {
      clearTimeout(timer);
      if (result === "timeout") notify.error(`${what}, but the job's revenue is still being updated after ${Math.round(lockTimeoutMs / 1000)} seconds. Open the job to check its revenue.`);
      st.running = null;
      if (st.latest) kickRevenue(quoteId);
      return result === "timeout" ? null : result;
    });
    st.running = run;
    // Landed late (after the limit): if a newer total was asked for since, it is synced
    // again, so an older write can't be the last one.
    sync.then(() => { if (st.requested !== total && !st.latest) { st.latest = { total: st.requested, what }; kickRevenue(quoteId); } });
    return run;
  };
  const afterSave = async (record, q, prevSaved, commit = null, { draft = false } = {}) => {
    const revenue = syncRevenueInOrder(record.id, record.total, draft ? "Draft saved" : "Quote saved");
    if (commit?.failed?.length) {
      notify.error(`Quote saved, but ${commit.failed.length} CRM record(s) could not be written. Check the People list.`);
    }
    await cacheImages(record, q.attachments);
    deleteDroppedFiles(prevSaved, record);
    await revenue;
  };

  // After a write: the builder takes the stored record when it still shows what was saved
  // (always, under the lock; after a time limit, only if nothing changed meanwhile). The
  // revision moves either way, so the next save checks against what is really stored.
  // The recovery copy goes only when the builder is clean; newer edits keep a copy, written
  // again against the new revision.
  // The lock held from the click to the write landing, so the builder still shows what
  // was saved: it takes the stored record, clean.
  const settleBuilder = (quoteId, record) => {
    const live = getQ();
    if (live?.id !== quoteId) return;
    const merged = mergedAfterSave(live, record);
    setQ(merged);
    markClean(merged, { savedAt: record.savedAt });
    retireRecoveryCopy();
  };

  // Throws on failure (the preview and the dialog show the message). Returns the record.
  const saveQuote = async () => {
    if (!isAdmin()) throw new Error("Quotes are admin-only.");
    if (busy) throw busyError();
    const q = getQ();
    const blocker = getSaveBlocker() || quoteSaveBlocker(q);
    if (blocker) throw new Error(blocker);
    return locked("saving", q.id, (current) => runIngest(q, current));
  };

  /* ── Save Draft ───────────────────────────────────────────────────── */
  const runSaveDraft = async (q) => {
    const base = baselineFor(q);
    const existingQuote = getQuotes().find((sq) => sq.id === q.id && isActiveQuoteRecord(sq));
    const prevSaved = recordOf(q.id);
    const isBooked = getJobs().some((j) => j.quoteId === q.id);
    const keepActive = !!existingQuote || isBooked || q.jobStatus === "booked";
    // Saving over an active quote follows the active-quote rule: it keeps a linked contact.
    // This path writes no CRM records, so only a stored link counts. A real draft can still
    // be saved without one.
    if (keepActive) {
      const block = quoteSaveBlocker(q) || quoteContactBlocker(q, getContacts(), { requireId: true });
      if (block) throw new Error(block);
    }
    // An assessment quote keeps its own draft flag (it is active either way).
    let record = buildDraftSaveRecord(q, { draft: keepActive ? !!(existingQuote?.fromAssessment && existingQuote.draft) : true });
    record = withSavedHistory(withListOwnedFields(record, { q, baseline: base.listFields, stored: prevSaved }));
    let committed;
    try {
      committed = await saveRecord(record, base.revision, unchangedListFields(q, base));
    } catch (e) {
      if (!e.userMessage) notify.error("Failed to save draft. Please try again.");
      throw e;
    }
    upsertSaved(committed);
    // A picture that hasn't uploaded exists only on screen and in the image cache: it is
    // cached before the builder moves on, and if that fails the draft stays on screen
    // with the picture, so nothing is lost.
    const kept = await keepPictures(q);
    if (getQ()?.id === q.id) {
      if (kept) {
        // The builder starts fresh after Save draft, as it always has.
        const blank = BLANK_QUOTE();
        setQ(blank);
        markClean(blank, { exists: false });
        clearHighlights();
        retireRecoveryCopy();
      } else {
        settleBuilder(q.id, committed);
      }
    }
    afterSave(committed, q, prevSaved, null, { draft: true }).catch((e) => console.error("After save:", e));
    if (!kept) {
      // The draft is stored, but not whole: the caller shows this instead of "Draft saved".
      const err = new Error("Draft saved, but a file that hasn't uploaded could not be kept with it, so the draft stays on screen. Wait for the upload, retry it, or remove the file before moving on.");
      err.userMessage = err.message;
      err.committed = committed;
      throw err;
    }
    return committed;
  };

  // Caches the pictures that have no stored file yet (the upload failed or hasn't run),
  // the only place their bytes are once the builder moves on. True when there are none
  // or the cache took them.
  const keepPictures = async (q) => {
    const pending = pendingFiles(q);
    if (!pending.length) { if (picturesUnkept === q.id) picturesUnkept = null; return true; }
    // Unkept until the cache has answered: a write still pending past the time limit
    // must count as unsaved work too, not only one that failed.
    picturesUnkept = q.id;
    // Only a picture with a preview can be cached; a PDF or a picture without one can't be
    // kept anywhere but on screen.
    const previews = pending.filter((a) => a.type === "image" && a.dataUrl).map((a) => ({ id: a.id, dataUrl: a.dataUrl }));
    if (previews.length < pending.length) return false;
    try {
      await saveImages(q.id, previews);
      if (picturesUnkept === q.id) picturesUnkept = null;
      return true;
    } catch (e) {
      console.warn("Image cache save:", e);
      return false;
    }
  };

  const saveDraft = async () => {
    if (!isAdmin()) throw new Error("Quotes are admin-only.");
    const q = getQ();
    return locked("saving", q.id, () => runSaveDraft(q));
  };

  // A quote with no active record and unsaved content files as a draft when the builder
  // moves on. Runs under the "saving" lock of a replacement.
  const preserveAsDraft = async (q) => {
    const base = baselineFor(q);
    const prevSaved = recordOf(q.id);
    const record = withSavedHistory(buildDraftSaveRecord(q, { draft: true }));
    let committed;
    try {
      committed = await saveRecord(record, base.revision, unchangedListFields(q, base));
    } catch (e) {
      console.warn("Auto-save draft failed:", e);
      throw e;
    }
    upsertSaved(committed);
    // The builder is about to be replaced: a picture that hasn't uploaded must be in the
    // cache first. The builder settles either way (the revision moved); if the cache
    // failed, the quote still counts as unsaved work (isDirty) and stays open with it.
    const kept = await keepPictures(q);
    settleBuilder(q.id, committed);
    deleteDroppedFiles(prevSaved, committed);
    if (!kept) {
      const err = new Error("The draft was saved, but a file that hasn't uploaded could not be kept with it, so the quote stays open. Wait for the upload, retry it, or remove the file first.");
      err.userMessage = err.message;
      throw err;
    }
    return committed;
  };

  /* ── List actions ─────────────────────────────────────────────────── */
  // Status, follow-up and trash changes from the lists write only their own fields. A
  // quote being saved, booked or restored waits; the row's buttons are disabled meanwhile.
  // The list writes still on their way, by quote: an opening of that quote waits for them
  // inside its lock, so the form and baseline it reads are what the store holds (the
  // optimistic row, undone if the write fails, is never a baseline).
  const pendingListWrites = new Map();
  const settledListWrites = (id) => (id && pendingListWrites.get(id)) || Promise.resolve();
  const writeFields = async (id, fields) => {
    const before = recordOf(id);
    const undo = Object.fromEntries(Object.keys(fields).map((k) => [k, before?.[k] ?? null]));
    const onList = (values) => setQuotes((prev) => prev.map((s) => (s.id === id ? { ...s, ...values } : s)));
    onList(fields);
    // Started only after the quote's earlier list write has settled, so two quick
    // actions (two snoozes) land in the order they were clicked.
    const write = settledListWrites(id).then(async () => {
      try {
        await storage.quotes.patch(id, fields);
      } catch (e) {
        onList(undo);
        console.error("Failed to update quote:", e);
        return false;
      }
      takeListFields(id, fields);
      return true;
    });
    pendingListWrites.set(id, write);
    write.then(() => { if (pendingListWrites.get(id) === write) pendingListWrites.delete(id); });
    return write;
  };
  // A list action's fields reaching the same quote open in the builder, from this
  // computer's own action or from the other computer's through the quotes listener
  // (followListSnapshot): the baseline takes every stored field (that is what the store
  // holds now), and the visible quote follows only where the builder hasn't edited the
  // field, so an edit to it stays an unsaved edit and is saved as such.
  const takeListFields = (id, fields) => {
    const live = getQ();
    if (live?.id !== id) return;
    const base = baselineFor(live);
    // Against the baseline quote itself, so a field outside the list-owned ones (booking
    // writes the contact link) is compared with what was loaded, not with "nothing".
    const follow = Object.fromEntries(Object.entries(fields).filter(([k]) => JSON.stringify(live[k] ?? null) === JSON.stringify(base.quote[k] ?? null)));
    if (Object.keys(follow).length) setQ({ ...live, ...follow });
    setBaseline({ ...base.quote, ...fields }, base.revision);
  };
  // The quotes listener's snapshot: the open quote's list-owned fields that moved in the
  // store since its baseline (Mark lost, a follow-up from the other computer) come in as
  // a list action would. Content changes don't: those are the revision check's.
  const followListSnapshot = (records) => {
    const live = getQ();
    if (!live?.id) return;
    const incoming = (records || []).find((r) => r.id === live.id);
    if (!incoming) return;
    const base = baselineFor(live);
    const moved = Object.fromEntries(LIST_OWNED_FIELDS
      .filter((f) => JSON.stringify(incoming[f] ?? null) !== JSON.stringify(base.listFields[f] ?? null))
      .map((f) => [f, incoming[f] ?? null]));
    if (Object.keys(moved).length) takeListFields(live.id, moved);
  };
  // A list action on the quote open in the builder holds the lock until the write has
  // landed and reached the builder (takeListFields), so an edit can't slip in between
  // the click and that reconciliation. On any other quote the row's own guard is enough.
  const patchQuoteFields = async (id, fields) => {
    if (!isAdmin() || !recordOf(id)) return false;
    if (isLocked(id)) { notify.error(BUSY_MESSAGE); return false; }
    if (getQ()?.id === id) {
      if (busy) { notify.error(BUSY_MESSAGE); return false; }
      return locked("updating", id, () => writeFields(id, fields));
    }
    return writeFields(id, fields);
  };

  // Booking reads the saved row, as it always has. It holds the lock on that quote until it
  // is done (a stuck one included), so a save or a second booking can't start meanwhile,
  // and `work` gets the stored row and a field writer.
  const freshRow = (row) => stripListRowKeys(recordOf(row?.id) || row);
  const whileBooking = (row, work) => {
    if (busy) throw busyError();
    return locked("booking", row.id, () => work(freshRow(row), (fields) => writeFields(row.id, fields)));
  };

  /* ── Replacing the builder ────────────────────────────────────────────
     Every path that replaces the open quote goes through here. A quote with no active
     record is preserved as a draft. Unsaved edits on a saved or booked quote stop for
     Save, Discard or Cancel; a booked quote is never saved just to allow navigating away.
     Returns whether the replacement went ahead (false: the quote stayed open). */
  // Discard in the dialog: the recovery copy is marked discarded only once the edits have
  // left the screen (the replacement landed, or discardEdits put the record back). If the
  // replacement fails, the edits stay on screen and their copy stays good.
  let discardPending = null;
  const askAboutEdits = async (q) => {
    const booked = getJobs().some((j) => j.quoteId === q.id) || q.jobStatus === "booked";
    const choice = await askUnsaved({ quote: q, booked, save: saveQuote });
    discardPending = choice === "discard" ? q.id : null;
    return choice;
  };
  const settleDiscard = (went) => {
    if (went && discardPending) discardRecoveryCopy(discardPending);
    discardPending = null;
  };

  const beginReplace = async () => {
    // A recovery copy still being checked against the store (a missing record on load)
    // is resolved first: it may install over the blank this replacement would take.
    if (recoveryPending) await recoveryPending;
    announceReplace();
    if (!isAdmin()) return true;
    if (busy) { notify.error(BUSY_MESSAGE); return false; }
    const q = getQ();
    if (!isDirty(q)) return true;
    if (isSavedOrBooked(q)) {
      const choice = await askAboutEdits(q);
      if (choice === "cancel") return false;
      // A write that waited out the save (an Add & link finishing) has landed by now and
      // counts as unsaved work; the quote stays so it can be seen and saved.
      if (choice === "saved" && isDirty(getQ())) {
        notify.info("The quote was saved, but a change landed just after (a link or a file finishing), so it stayed open. That change isn't saved yet.");
        return false;
      }
      return true;
    }
    try {
      await locked("saving", q.id, () => preserveAsDraft(q));
    } catch (e) {
      notify.error(e?.userMessage || "The open quote could not be saved as a draft, so it stayed open. Nothing was lost.");
      return false;
    }
    // As after the dialog's Save: a write that waited out the filing and landed is unsaved
    // work, so the quote stays.
    if (isDirty(getQ())) {
      notify.info("The quote was saved as a draft, but a change landed just after (a link or a file finishing), so it stayed open. That change isn't saved yet.");
      return false;
    }
    return true;
  };

  // `clean`: the installed quote is what its record holds. `baseline`: the installed quote
  // starts out differing from a record (an amendment), and that record's form and revision
  // are what its edits are judged and saved against. Neither: a quote with no record yet.
  // `build` runs after the dialog, so it reads the record as the dialog's Save left it. It
  // returns the quote to install, or { next, baseline } when the installed quote starts
  // out differing from a record (an amendment).
  const replaceWith = async (build, { clean: isClean, savedAt = null, targetId = null }) => {
    if (!(await beginReplace())) return false;
    const went = await replaceLocked(build, { isClean, savedAt, targetId });
    settleDiscard(went);
    return went;
  };
  const replaceLocked = async (build, { isClean, savedAt, targetId }) => {
    const outgoingId = getQ()?.id;
    try {
      return await locked("opening", outgoingId, async (current) => {
        const built = await build();
        if (!current()) return false;
        // The record being opened is locked with the outgoing quote; this is the check
        // behind that lock, in case it left the list anyway.
        if (targetId && !recordOf(targetId)) {
          notify.error("That quote is no longer in the list, so it was not opened.");
          return false;
        }
        const next = built?.next && built?.baseline ? built.next : built;
        const baseline = built?.next && built?.baseline ? built.baseline : null;
        // The builder must still hold the quote this replacement started from: nothing
        // installs under a lock, but this is the check behind that.
        if (getQ()?.id !== outgoingId) {
          notify.error("The quote on screen changed while this was opening, so it was not opened.");
          return false;
        }
        setQ(next);
        picturesUnkept = null;
        // A copy written for the outgoing quote while this opening was stuck held the
        // writes waiting for it. Another quote open now: they are dropped, so that copy
        // is marked discarded once its write has settled, unless a newer copy has started
        // writing since (a later edit's; the generation guard). The same quote open as an
        // amendment: the copy is written again from the amended form, since the stuck
        // message promised the edits are kept.
        if (stuckCopy && stuckCopy.quoteId === outgoingId) {
          const { quoteId, write } = stuckCopy;
          const sameId = outgoingId === next.id;
          stuckCopy = null;
          const gen = recoveryGen;
          // The amended copy is the one that must stand: the dialog's Discard marker,
          // which would be written after it over the same key, is not written (the
          // discarded edits are gone from the copy either way).
          if (sameId) discardPending = null;
          write.catch(() => {}).then(() => {
            if (recoveryGen !== gen) return;
            if (sameId) { const q = getQ(); if (q?.id === quoteId) writeRecoveryCopy(withDeferred(q)); }
            else discardRecoveryCopy(quoteId);
          });
        }
        if (baseline) setBaseline({ ...baseline.quote, id: next.id }, baseline.revision);
        else if (isClean) markClean(next, { savedAt: savedAt ?? recordOf(next.id)?.savedAt ?? null, exists: !!recordOf(next.id) });
        else clean = null;
        // A list action that reached the list while the build waited (the image cache)
        // comes in now, as it would have after: content and revision stay the snapshot's.
        followListSnapshot(getQuotes());
        clearHighlights();
        return true;
      }, { targetId });
    } catch (e) {
      notify.error(e?.userMessage || e?.message || "The quote could not be opened.");
      return false;
    }
  };

  // Throws away the open quote's unsaved edits: the STORED record comes back as loaded. It
  // is read from the store, not the list, so Discard after "saved elsewhere" loads the
  // newer version even before the listener has delivered it; a failed read keeps the
  // edits and says so. The list takes the record read. (New on a quote with no record
  // uses Discard the same way: it starts over.)
  const discardEdits = async () => {
    if (busy) { notify.error(BUSY_MESSAGE); return false; }
    const q = getQ();
    // Discard intent, from here on: a lock stuck before the record is back says the
    // discarded edits are still in the copy and may be offered again (as a replacement
    // after the dialog's Discard does).
    discardPending = q?.id || null;
    const went = await locked("opening", q?.id, async (current) => {
      let record = null;
      // A quote with a record here, or one that had a record when it was loaded (its row
      // may have left the list since): read the store.
      if (q?.id && (recordOf(q.id) || hadRecord(q))) {
        let stored;
        try {
          stored = await storage.quotes.fetch(q.id);
        } catch (e) {
          console.warn("Discard edits read:", e);
          notify.error("The saved quote could not be read, so the edits were kept. Check the connection and try again.");
          return false;
        }
        if (stored) {
          record = migrateQuote(stored);
        } else {
          // Deleted elsewhere since it was opened: nothing to go back to.
          notify.info("That quote was deleted elsewhere since it was opened, so the edits were thrown away and the builder is empty.");
          setQuotes((prev) => prev.filter((s) => s.id !== q.id));
        }
      }
      const formOf = (r) => (isActiveQuoteRecord(r) ? loadedForm(r) : draftForm(r));
      let next = record ? await withCachedImages(record.id, formOf(record)) : BLANK_QUOTE();
      if (!current()) return false;
      // The store may have moved on while the image cache was read (a save or a delete
      // from the other computer): it is read once more after the wait, and what it holds
      // now is what the builder goes back to. A failed second read keeps the first.
      if (record) {
        let again = record;
        try { const s = await storage.quotes.fetch(record.id); again = s ? migrateQuote(s) : null; } catch (e) { console.warn("Discard edits re-read:", e); }
        if (!current()) return false;
        if (!again) {
          notify.info("That quote was deleted elsewhere since it was opened, so the edits were thrown away and the builder is empty.");
          setQuotes((prev) => prev.filter((s) => s.id !== record.id));
          record = null;
          next = BLANK_QUOTE();
        } else {
          // The later read is the record (a save or a list action meanwhile included); the
          // previews the cache gave stay on the pictures.
          if (again !== record) {
            const previews = Object.fromEntries((next.attachments || []).filter((a) => a?.dataUrl).map((a) => [a.id, a.dataUrl]));
            record = again;
            next = formOf(again);
            next.attachments = (next.attachments || []).map((a) => (previews[a.id] ? { ...a, dataUrl: previews[a.id] } : a));
          }
          upsertSaved(record);
        }
      }
      if (next.attachments) next.attachments = normalizeLoadedAttachments(next.attachments);
      setQ(next);
      // The stored form is back on screen: a file the cache refused is gone with the
      // edits, so the quote is as clean as the record.
      picturesUnkept = null;
      markClean(next, { savedAt: record?.savedAt ?? null, exists: !!record });
      // A list action that reached the list while the image cache was read comes in now,
      // as after an open (the revision stays the record's: list actions don't move it).
      followListSnapshot(getQuotes());
      clearHighlights();
      return true;
    });
    // The edits are off the screen: their recovery copy is marked discarded now, not
    // before (a failed read keeps the edits, and their copy with them). Not when a write
    // that waited out the lock just landed (a link or a file finishing): that is unsaved
    // work, and the autosave writes its copy over the old one.
    if (went && !isDirty(getQ())) discardRecoveryCopy(q?.id);
    discardPending = null;
    return went;
  };

  /* ── Loads ────────────────────────────────────────────────────────── */
  const loadQuote = async (row) => {
    const live = getQ();
    // The quote already open with unsaved edits: show it as it is, nothing to replace.
    if (live?.id === row?.id && isDirty(live) && !busy) {
      notify.info("Showing this quote with the edits that aren't saved yet.");
      return true;
    }
    return replaceWith(async () => {
      // Read after the dialog: its Save may have just written this record. Content and
      // revision come from this one snapshot, whatever the list holds by the time the
      // image cache has answered. A list write for this quote still on its way lands
      // first (or is undone), so the baseline is what the store holds.
      await settledListWrites(row?.id);
      const saved = freshRow(row);
      const loaded = loadedForm(saved);
      if (loaded.attachments) loaded.attachments = normalizeLoadedAttachments(loaded.attachments);
      const next = await withCachedImages(saved.id, loaded);
      return { next, baseline: { quote: next, revision: recordRevision(saved) } };
    }, { clean: true, targetId: row?.id });
  };

  const duplicateQuote = (row) => replaceWith(async () => {
    await settledListWrites(row?.id);
    const saved = freshRow(row);
    // A duplicate is a brand-new OPEN quote: never the source's identity, lifecycle,
    // history or change-order parentage (duplicateQuoteData).
    const loaded = duplicateQuoteData(saved);
    loaded.lineGroups = quoteLineGroups(saved);
    if (loaded.attachments) loaded.attachments = normalizeLoadedAttachments(loaded.attachments);
    return withCachedImages(saved.id, loaded);
    // Not clean: a duplicate has no record yet, so it files as a draft if the builder moves on.
  }, { clean: false });

  // An amendment is the stored quote with the job's schedule pulled in and a version added.
  // Not clean: until it is saved, the amendment doesn't exist, so leaving asks.
  const amendQuote = (row, job = null) => replaceWith(async () => {
    // Read after the dialog: its Save may have just written this record, and the
    // amendment and its baseline both come from that record.
    await settledListWrites(row?.id);
    const saved = freshRow(row);
    const version = snapshotQuoteForVersion(saved, { source: "amend", note: "Snapshot before amendment" });
    const versions = [...(saved.versions || []), version];
    const overlay = overlayJobIntoQuote(saved, job);
    const mergedSessions = overlay.sessions || saved.sessions || [];
    // Single-day quotes persist no sessions: derive the travel clause from the job-level
    // studio state (job wins when overlaying), not from an empty list.
    const sections = applyTravelRule(
      saved.sections || initSections(),
      mergedSessions.length ? mergedSessions : [{ isStudio: job ? !!job.isStudio : !!saved.isStudio }],
    );
    const loaded = {
      ...loadedForm(saved), ...overlay,
      versions,
      sections,
      amendPulledJobData: !!job,
    };
    // A quote from before the contact rule may have only a typed name while its booked
    // job holds the link: take the job's contact (name, link, email).
    Object.assign(loaded, amendContact(loaded, job, getContacts()));
    if (loaded.attachments) loaded.attachments = normalizeLoadedAttachments(loaded.attachments);
    const next = await withCachedImages(saved.id, loaded);
    return { next, baseline: { quote: loadedForm(saved), revision: recordRevision(saved) } };
  }, { clean: false, targetId: row?.id });

  const createChangeOrderQuote = (job, originalQuote) => replaceWith(() => {
    const now = new Date().toISOString().split("T")[0];
    return {
      ...BLANK_QUOTE(),
      step: "review",
      docType: "change_order",
      clientName: job.clientName || "",
      // The contact carries over as one person (changeOrderContact).
      ...changeOrderContact(job, originalQuote, getContacts()),
      industry: job.industry || "art",
      jobLocation: job.location || "",
      isStudio: job.isStudio || false,
      scanDate: "",
      summary: `Change Order for ${job.clientName || ""}${job.jobId ? " (" + job.jobId + ")" : ""}. Original job: ${job.description || ""}`,
      quoteDate: now,
      followUpDate: defaultFollowUp(now),
      changeOrderOfJobId: job.id,
      changeOrderOfQuoteId: originalQuote?.id || job.quoteId || null,
      lineItems: [],
      savedToDb: false,
    };
  }, { clean: false });

  // Website inquiry -> pre-filled builder state (prospectQuote.js). The open quote is kept
  // first, like any replacement; a deep link can never destroy unsaved work.
  const startQuoteFromProspect = async (raw) => {
    // Converting the same inquiry twice used to mint a second unrelated quote (and a second
    // set of CRM records). sourceProspectId is the idempotency key.
    const prospectId = raw?._docId || raw?.id || null;
    const already = prospectId ? getQuotes().find((qq) => qq.sourceProspectId === prospectId && !qq.deletedAt) : null;
    if (already) {
      const reopened = await loadQuote(already);
      if (reopened) notify.info("This inquiry was already converted. Reopening the existing quote.");
      return reopened;
    }
    return replaceWith(() => mapProspectToQuote(raw, { contacts: getContacts(), clients: getClients() }), { clean: false });
  };

  const newQuote = () => replaceWith(() => BLANK_QUOTE(), { clean: true });

  const loadDraft = async (draftId) => {
    const found = recordOf(draftId);
    if (!found) return false;
    const live = getQ();
    if (live?.id === draftId && isDirty(live) && !busy) {
      notify.info("Showing this draft with the edits that aren't saved yet.");
      return true;
    }
    return replaceWith(async () => {
      await settledListWrites(draftId);
      const draft = recordOf(draftId) || found;
      const next = await withCachedImages(draft.id, draftForm(draft));
      return { next, baseline: { quote: next, revision: recordRevision(draft) } };
    }, { clean: true, targetId: draftId });
  };

  /* ── Restore ──────────────────────────────────────────────────────── */
  const runRestore = async (quoteId, versionId, current) => {
    const saved = getQuotes().find((s) => s.id === quoteId && !s.deletedAt);
    if (!saved) { notify.error("Could not find that quote."); throw new Error("Quote not found"); }
    const target = (saved.versions || []).find((v) => v.id === versionId);
    if (!target) { notify.error("Could not find that quote version."); throw new Error("Quote version not found"); }
    if (isLegacyVersion(target)) { notify.error("Legacy quote versions are view-only."); throw new Error("Legacy quote versions are view-only"); }
    if (isMarkerVersion(target)) { notify.error("Restore audit markers are view-only."); throw new Error("Restore audit markers are view-only"); }
    if (saved.changeOrderOfJobId || saved.changeOrderOfQuoteId || target.changeOrderOfJobId || target.changeOrderOfQuoteId) {
      notify.error("Change-order quote restore is not available yet.");
      throw new Error("Change-order quote restore is blocked");
    }

    // The restored quote keeps a valid linked contact: the version's own, else the quote's
    // current one (a version from before the link, or one whose contact was deleted, must
    // not take it away).
    const restored = applyVersionToQuote(stripListRowKeys(saved), target);
    const contactFields = restoreContactFields(saved, restored, getContacts(), target);
    if (!contactFields) {
      notify.error("Link this quote's contact first (Edit the quote), then restore the version.");
      throw new Error("Quote restore needs a linked contact");
    }
    const pendingRestore = { ...restored, ...contactFields };
    const freshTotals = calcTotals(pendingRestore.lineItems || [], pendingRestore.quoteDiscounts || []);
    const preRestoreSnapshot = snapshotQuoteForVersion(saved, { source: "pre-restore", preRestoreOfVersionId: versionId, note: "Snapshot before restore" });
    const postRestoreMarker = {
      schemaVersion: 2, id: mkVersionId(), source: "post-restore-marker", markerOnly: true,
      restoredFromVersionId: versionId, timestamp: new Date().toISOString(), note: "Quote restored from prior version",
    };
    const record = {
      ...pendingRestore,
      lineItems: (pendingRestore.lineItems || []).map(({ key: _key, ...item }) => item),
      quoteDiscounts: (pendingRestore.quoteDiscounts || []).map(({ key: _key, ...discount }) => discount),
      subtotal: freshTotals.subtotal,
      totalSavings: freshTotals.totalSavings,
      total: freshTotals.total,
      mathVerified: true,
      versions: [...(saved.versions || []), preRestoreSnapshot, postRestoreMarker],
      savedAt: new Date().toISOString(),
    };

    // Checked against the record the restore was built from (the list's). The builder's
    // own edits, if any, were saved or discarded by the dialog before this started.
    const expected = recordRevision(saved);
    let committed;
    try {
      // A restore brings the version's status, follow-up and trash state back as the
      // version's own content, so nothing is kept from the stored document here.
      // A restore brings content back, never the list's state: a Mark lost, a follow-up or
      // a Trash that landed meanwhile (they don't move the revision) stays as stored.
      committed = await saveRecord(record, expected, LIST_OWNED_FIELDS);
    } catch (e) {
      if (!e.userMessage) notify.error("Failed to restore quote. Please try again.");
      throw e;
    }
    setQuotes((prev) => prev.map((item) => (item.id === quoteId ? committed : item)));
    // The open builder takes the restored record, under the lock held until here.
    if (getQ()?.id === quoteId) {
      const merged = { ...getQ(), ...committed, lineItems: pendingRestore.lineItems, quoteDiscounts: pendingRestore.quoteDiscounts, savedToDb: true };
      setQ(merged);
      markClean(merged, { savedAt: committed.savedAt });
      retireRecoveryCopy();
    }

    return committed;
  };

  // The job's revenue after a restore, outside the lock: the restore is done once its
  // record is stored and the builder settled, and this can't turn it into a time limit.
  const revenueAfterRestore = async (record) => {
    const revenueSync = await syncRevenueInOrder(record.id, record.total, "Quote restored");
    return { success: true, quote: record, revenueSync, revenueSyncSkipped: !!revenueSync?.skipped, restoredTotal: record.total };
  };

  const restoreQuoteVersion = async (quoteId, versionId) => {
    if (!isAdmin()) throw new Error("Quotes are admin-only.");
    if (busy) throw busyError();
    // Restore replaces the open quote when it is the one restored, so unsaved edits on it
    // stop first, the same as any other replacement.
    const open = getQ();
    if (open?.id === quoteId && isDirty(open)) {
      announceReplace();
      const choice = await askAboutEdits(open);
      if (choice === "cancel") return { success: false, cancelled: true };
      // As in beginReplace: a write that waited out the dialog's Save and landed is
      // unsaved work, so the quote stays and nothing is restored over it.
      if (choice === "saved" && isDirty(getQ())) {
        notify.info("The quote was saved, but a change landed just after (a link or a file finishing), so nothing was restored. That change isn't saved yet.");
        return { success: false, cancelled: true };
      }
      // Discarded edits leave the screen before the restore starts, so a restore that
      // passes the time limit finds a clean builder and never writes them to the
      // recovery copy (a replacement's "opening" lock never writes one; this lock would).
      if (choice === "discard" && !(await discardEdits())) return { success: false, cancelled: true };
      // A write that waited out the discard (a link or a file finishing) landed when its
      // lock lifted: unsaved work, so nothing is restored over it.
      if (choice === "discard" && isDirty(getQ())) {
        notify.info("The edits were discarded, but a change landed just after (a link or a file finishing), so nothing was restored. That change isn't saved yet.");
        return { success: false, cancelled: true };
      }
    }
    const committed = await locked("restoring", quoteId, (current) => runRestore(quoteId, versionId, current));
    return revenueAfterRestore(committed);
  };

  /* ── What the screens ask ─────────────────────────────────────────── */
  // Saved Quotes and the job card act on the saved record. When the builder holds unsaved
  // edits to that same quote, their PDF and their Book Job don't see them, so they say so.
  const unsavedEditsNotice = (quoteId) => {
    const q = getQ();
    return quoteId && q?.id === quoteId && isDirty(q) ? UNSAVED_EDITS_NOTICE : "";
  };

  return {
    // saves and writes
    saveQuote, saveDraft, patchQuoteFields, whileBooking, updateQuote, normalizeOpenQuote,
    // replacing the builder
    loadQuote, loadDraft, duplicateQuote, amendQuote, createChangeOrderQuote, startQuoteFromProspect, newQuote, discardEdits,
    restoreQuoteVersion, onReplaceStart,
    // the recovery copy
    shouldAutosave, writeRecoveryCopy, installRecoveredCopy, retireRecoveryCopy, retireIfClean, recoveryCopyFor,
    // state
    isDirty, hadRecord, isSavedOrBooked, getBusy, isLocked, unsavedEditsNotice, baselineFor, isOpenQuote, followListSnapshot, recoveryUnknown,
  };
}

/** Opens a screen after replacing the builder's quote; goes back to the builder when the
 *  replacement didn't happen (Cancel in the dialog, a save still running, a failed draft). */
export const openUnlessCancelled = async (replace, open, back) => {
  const went = await replace();
  if (went === false) back?.();
  else open?.();
};
